import type { ListBuilderColumn } from '../../types';
import { SENSOR_DEVICE_STATUS_VALUE_MAPPING } from './shared';

/** Device id (link to detail) with the device's friendly name as subtext. */
export const createDeviceColumn = (): ListBuilderColumn => ({
	id: 'sensor-device-id',
	headers: [{ id: 'sensor-device-id-header', text: 'Device' }],
	fields: [
		{
			id: 'device-id-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'deviceId', objectTable: 'sensor_device' },
			},
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'text',
			link: { type: 'object', enabled: true },
		},
		{
			id: 'line-break-1',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
		},
		{
			id: 'device-name-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'name', objectTable: 'sensor_device' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
	],
	sortable: true,
});

/**
 * Merchandiser the device is attached to (or "Unattached"), with the unit's
 * serial number underneath (PD-15562) — two units of the same model routinely
 * share a name, so the serial is what identifies the physical box.
 */
export const createDeviceMerchandiserColumn = (): ListBuilderColumn => ({
	id: 'sensor-device-merchandiser',
	headers: [{ id: 'sensor-device-merchandiser-header', text: 'Merchandiser' }],
	fields: [
		{
			id: 'merchandiser-label-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'merchandiserLabel', objectTable: 'sensor_device' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		{
			id: 'merchandiser-line-break',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
		},
		{
			id: 'merchandiser-serial-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'merchandiserSerialLabel',
					objectTable: 'sensor_device',
				},
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	minWidth: 180,
});

/**
 * Where the unit physically is (PD-15562): the servicing warehouse, or the
 * customer it is placed at with the street address underneath. Devices with no
 * merchandiser read "Unassigned" rather than showing an empty cell.
 */
export const createDevicePlacementColumn = (): ListBuilderColumn => ({
	id: 'sensor-device-placement',
	headers: [{ id: 'sensor-device-placement-header', text: 'Placement' }],
	fields: [
		{
			id: 'placement-label-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'placementLabel', objectTable: 'sensor_device' },
				defaultValue: 'Unassigned',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
		{
			id: 'placement-line-break',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
		},
		{
			id: 'placement-detail-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'placementDetail', objectTable: 'sensor_device' },
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	minWidth: 220,
});

/**
 * Status and last-seen in one cell (PD-15562) — they only ever get read
 * together, and the badge renders `healthState`, which already accounts for
 * staleness and placement (see SENSOR_DEVICE_STATUS_VALUE_MAPPING).
 */
export const createDeviceStatusColumn = (): ListBuilderColumn => ({
	id: 'sensor-device-status',
	headers: [{ id: 'sensor-device-status-header', text: 'Status' }],
	fields: [
		{
			id: 'status-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'healthState', objectTable: 'sensor_device' },
			},
			styleConfig: {
				size: 'sm',
				valueMapping: [...SENSOR_DEVICE_STATUS_VALUE_MAPPING],
			},
			displayStyle: 'badge',
		},
		{
			id: 'status-line-break',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
		},
		{
			id: 'last-seen-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'lastSeenLabel', objectTable: 'sensor_device' },
				defaultValue: 'Never',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
	],
	sortable: true,
	// The badge is derived, so the header sorts by the column it is derived
	// from — most-recently-heard-from first.
	sortFields: [
		{ direction: 'DESC', fieldName: 'lastSeen', objectTable: 'sensor_device' },
	],
	minWidth: 150,
});

/**
 * Device health in one cell (PD-15562): assigned-of-total sensor coverage plus
 * battery, e.g. "4 / 4 · 4.17V". Orphans show as assigned < total.
 */
export const createDeviceHealthColumn = (): ListBuilderColumn => ({
	id: 'sensor-device-health',
	headers: [{ id: 'sensor-device-health-header', text: 'Sensors / Battery' }],
	fields: [
		{
			id: 'health-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'healthLabel', objectTable: 'sensor_device' },
				defaultValue: '0 / 0',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 160,
});

export const createSensorDeviceColumns = (): Array<ListBuilderColumn> => [
	createDeviceColumn(),
	createDeviceMerchandiserColumn(),
	createDevicePlacementColumn(),
	createDeviceStatusColumn(),
	createDeviceHealthColumn(),
];
